import { Link } from 'react-router-dom'
import { BookOpen, Target, Users, FileText, Trophy, TrendingUp } from 'lucide-react'
import { getProgressSummary } from '../utils/storage'

export default function Home({ state }) {
  const progress = getProgressSummary(state)

  const modules = [
    {
      title: 'Practice Mode',
      description: 'Practice with immediate feedback on OIR, WAT, TAT, SRT modules',
      icon: BookOpen,
      link: '/practice',
      color: 'bg-blue-500',
    },
    {
      title: 'Test Mode',
      description: 'Take timed, simulated tests with randomized questions',
      icon: Target,
      link: '/test',
      color: 'bg-green-500',
    },
    {
      title: 'Mock Interview',
      description: 'Practice interview questions and prepare your responses',
      icon: Users,
      link: '/interview',
      color: 'bg-purple-500',
    },
    {
      title: 'Defence Notes',
      description: 'Study materials for Army, Navy, and Air Force',
      icon: FileText,
      link: '/notes',
      color: 'bg-orange-500',
    },
  ]

  const newFeatures = [
    {
      title: 'Daily Defence News 🆕',
      description: 'Stay updated with latest defence news and current affairs questions',
      link: '/news',
      badge: 'NEW',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Question Manager',
      description: 'Add and manage OIR questions with images for daily practice',
      link: '/question-manager',
      badge: 'ADMIN',
      color: 'from-blue-500 to-indigo-600'
    }
  ]

  return (
    <div className="space-y-8 animate-slide-in">
      {/* Hero Section */}
      <section className="text-center py-12 bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl text-white shadow-xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Welcome to SSB Prep Platform
        </h1>
        <p className="text-xl md:text-2xl mb-8 text-primary-100">
          Complete preparation for Services Selection Board
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/practice" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all shadow-md hover:shadow-lg">
            Start Practicing
          </Link>
          <Link to="/test" className="bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-800 transition-all shadow-md hover:shadow-lg border-2 border-white">
            Take a Test
          </Link>
        </div>
      </section>

      {/* Progress Summary */}
      <section className="card">
        <div className="flex items-center mb-4">
          <Trophy className="w-6 h-6 text-yellow-500 mr-2" />
          <h2 className="text-2xl font-bold">Your Progress</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {progress.totalTests}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Tests Taken</div>
          </div>
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {progress.oirCompleted}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">OIR Completed</div>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {progress.tatStories}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">TAT Stories</div>
          </div>
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {progress.interviewSessions}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Mock Interviews</div>
          </div>
        </div>
        {progress.lastActivity && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1" />
            Last activity: {new Date(progress.lastActivity).toLocaleDateString()}
          </p>
        )}
      </section>

      {/* New Features Highlight */}
      <section className="card bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 border-2 border-orange-300 dark:border-orange-700">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-orange-600" />
          ✨ New Features Added!
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {newFeatures.map((feature, index) => (
            <Link
              key={index}
              to={feature.link}
              className="p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all border-2 border-transparent hover:border-primary-400"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-bold">{feature.title}</h3>
                <span className={`px-2 py-1 text-xs font-bold bg-gradient-to-r ${feature.color} text-white rounded-full animate-pulse`}>
                  {feature.badge}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Module Cards */}
      <section>
        <h2 className="text-2xl font-bold mb-6">Quick Access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module, index) => {
            const Icon = module.icon
            return (
              <Link
                key={index}
                to={module.link}
                className="card hover:scale-105 transform transition-all cursor-pointer"
              >
                <div className={`${module.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">{module.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {module.description}
                </p>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Information Section */}
      <section className="grid md:grid-cols-2 gap-6">
        <div className="card border-l-4 border-primary-600">
          <h3 className="text-xl font-bold mb-3">About SSB</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Services Selection Board (SSB) is a 5-day process to assess candidates for entry into the Indian Armed Forces.
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li>• Day 1: Screening (OIR, PPDT)</li>
            <li>• Day 2-4: Psychological Tests (TAT, WAT, SRT, SD)</li>
            <li>• Day 3-4: Group Testing (GD, GPE, GTO)</li>
            <li>• Day 5: Conference & Medical</li>
          </ul>
        </div>

        <div className="card border-l-4 border-green-600">
          <h3 className="text-xl font-bold mb-3">Features</h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Comprehensive practice modules for all SSB tests</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Timed test mode with randomized questions</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Offline support with service worker caching</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Progress tracking with local storage</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              <span>Defence notes for Army, Navy & Air Force</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  )
}
